import { useEffect, useRef } from 'react'
import { useAuthStore } from '../store/authStore'
import { useLibraryStore } from '../store/libraryStore'
import { createChapterNotification } from '../api/social'

const CHECK_KEY   = (userId) => `nakama-ch-check-ts-${userId}`
const SEEN_KEY    = (id) => `nakama-ch-seen-${id}`
const ONE_DAY     = 86_400_000
const SAFE_RATINGS = ['safe', 'suggestive', 'erotica'].map(r => `contentRating[]=${r}`).join('&')

// Concurrency cap keeps MangaDex happy while still clearing a large library
// in seconds rather than minutes.
const CONCURRENCY = 4

async function checkOne(item, userId) {
  try {
    const res = await fetch(
      `/api/mangadex/chapter?manga=${item.content_id}&limit=1&translatedLanguage[]=en&order[chapter]=desc&${SAFE_RATINGS}`
    )
    if (!res.ok) return
    const data = await res.json()
    const latest = data.data?.[0]
    if (!latest) return

    const latestNum = parseFloat(latest.attributes.chapter) || 0
    const seenNum   = parseFloat(localStorage.getItem(SEEN_KEY(item.content_id)) || '0')

    // Only notify on a genuinely new chapter (seenNum > 0 prevents first-run flood)
    if (latestNum > seenNum && seenNum > 0) {
      await createChapterNotification(
        userId, item.title, latest.attributes.chapter,
        item.content_id, latest.id,
      )
    }
    if (latestNum > seenNum) {
      localStorage.setItem(SEEN_KEY(item.content_id), String(latestNum))
    }
  } catch {
    // per-manga errors are non-fatal
  }
}

export function useChapterNotify() {
  const { user } = useAuthStore()
  const { items } = useLibraryStore()
  const ranForUser = useRef(null)

  useEffect(() => {
    if (!user || !items.length) return

    // Only run once per mount per user — the items.length dep is only here so
    // we re-evaluate when the library finally loads, not to re-trigger scans.
    if (ranForUser.current === user.id) return

    const lastCheck = parseInt(localStorage.getItem(CHECK_KEY(user.id)) || '0')
    if (Date.now() - lastCheck < ONE_DAY) {
      ranForUser.current = user.id
      return
    }

    const readingManga = items.filter(i => i.content_type === 'manga' && i.status === 'reading')
    if (!readingManga.length) return

    ranForUser.current = user.id
    localStorage.setItem(CHECK_KEY(user.id), String(Date.now()))

    let cancelled = false
    const queue = [...readingManga]
    const worker = async () => {
      while (!cancelled && queue.length) {
        const item = queue.shift()
        await checkOne(item, user.id)
      }
    }
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker)
    Promise.all(workers).catch(() => {})

    return () => { cancelled = true }
  }, [user?.id, items.length])
}
